import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { subMinutes } from 'date-fns';
import useTodaysAppointments from '@hooks/useTodaysAppointments';
import MediaPermissionsWarning from './MediaPermissionsWarning';

interface AppointmentWithDates {
  appt_start_date: string | Date;
  appt_end_date: string | Date;
  call_type_code?: string;
  appointment_id?: number;
}


const isInCallingWindow = (appointment: AppointmentWithDates, now: Date) => {
  const callingWindowStart = subMinutes(new Date(appointment.appt_start_date), 10);
  const callingWindowEnd = new Date(appointment.appt_end_date);

  return now >= callingWindowStart && now <= callingWindowEnd;
};

/**
 * Patient-specific wrapper for MediaPermissionsWarning.
 * Calculates calling window based on today's appointments (10 minutes before to 20 minutes after VIDEO/AUDIO appointment).
 * Shows banner on all patient routes except waiting room.
 * Uses the same timing mechanism as UpcomingAppointment timer display.
 */
const PatientMediaPermissionsWarning = () => {
  const router = useRouter();
  const isPatientRoute = router.pathname.startsWith('/patients');
  const isWaitingRoom = router.pathname.includes('/waiting-room');
  const { appointments } = useTodaysAppointments();

  const [, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTick(prev => prev + 1);
    }, 30000);

    return () => clearInterval(interval);
  }, []);
  // Get the appointment currently in calling window with priority for newest ones
  const now = new Date();
  const activeAppointments = appointments
    ?.filter(appt => appt.call_type_code)
    ?.filter(appt => isInCallingWindow(appt, now));

  // Priority: select the appointment that most recently entered calling window
  // (i.e., the one with the latest calling window start time that has already passed)
  // Only appointments whose calling window has started
  // Latest calling window start first
  const upcomingAppointment = activeAppointments
    ?.map(appt => ({
      ...appt,
      callingWindowStart: subMinutes(new Date(appt.appt_start_date), 10)
    }))
    ?.filter(appt => now >= appt.callingWindowStart)
    ?.sort((a, b) => b.callingWindowStart.getTime() - a.callingWindowStart.getTime())[0];


  if (!isPatientRoute || isWaitingRoom) {
    return null;
  }

  const shouldShowBanner = Boolean(
    upcomingAppointment &&
    upcomingAppointment.call_type_code !== 'AUDIO'
  );

  const storageKey = upcomingAppointment?.appointment_id
    ? `patient-${upcomingAppointment.appointment_id}`
    : undefined;

  const callType = upcomingAppointment?.call_type_code as 'AUDIO' | 'VIDEO' | undefined;

  return (
    <MediaPermissionsWarning
      key={storageKey}
      isInCallingWindow={shouldShowBanner}
      storageKey={storageKey}
      callType={callType}
      style={{ width: '100%', marginBottom: '20px' }}
    />
  );
};

export default PatientMediaPermissionsWarning;
